import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import "../styles/NewUsersPage.css";

const RegistrationPage = () => {
  const [step, setStep] = useState(1);

  // Step 1 (obsession)
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [optionLInput, setOptionLInput] = useState("");

  // Step 2 (email only)
  const [email, setEmail] = useState("");

  // UI state
  const [emailError, setEmailError] = useState("");
  const [emailExistsError, setEmailExistsError] = useState("");
  const [loading, setLoading] = useState(false);
  const [submittedStep2, setSubmittedStep2] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const postSignupRedirect = location.state?.postSignupRedirect;

  const options = [
    { title: "art & photography", description: "original pieces, prints, posters, photos, kids’ artwork", emoji: "🖼️" },
    { title: "cards & games", description: "pokémon, magic, tarot, trading cards, board games", emoji: "🃏" },
    { title: "classics", description: "books, coins, stamps, stickers, watches, wine, cars", emoji: "📚" },
    { title: "comics & manga", description: "graphic novels, single issues, imports, anime series", emoji: "🦸‍♀️" },
    { title: "fashion", description: "vintage pieces, shoes, sneakers, bags, jewellery, hats, caps", emoji: "🛍️" },
    { title: "home", description: "ceramics, plates, furniture, teacups, glassware, clutter", emoji: "🏺" },
    { title: "music", description: "vinyl, instruments, cds, cassettes, band tees, concert tickets", emoji: "🎧" },
    { title: "sports & adventure", description: "jerseys, gear, memorabilia, autographs, milestones", emoji: "⛰" },
    { title: "travel ephemera", description: "postcards, maps, tickets, boarding passes, currency, magnets", emoji: "🧳" },
    { title: "toys", description: "lego, action figures, dolls, beanie babies, miniatures", emoji: "🧸" },
    { title: "my collection isn't listed", description: "tell us more, please!", emoji: "🧐" },
    { title: "i don't identify as a collector", description: "yet!", emoji: "👀" }
  ];

  const toggleOption = (optionTitle) => {
    setSelectedOptions((prev) => {
      if (optionTitle === "i don't identify as a collector") {
        return prev.includes(optionTitle) ? [] : [optionTitle];
      }
      if (prev.includes("i don't identify as a collector")) {
        return [...prev.filter((item) => item !== "i don't identify as a collector"), optionTitle];
      }
      return prev.includes(optionTitle)
        ? prev.filter((item) => item !== optionTitle)
        : [...prev, optionTitle];
    });
  };

  const saveStepData = async (data) => {
    try {
      await fetch("https://squid-app-6s2hc.ondigitalocean.app/save-step", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
    } catch (error) {
      console.error("Error saving step data:", error);
    }
  };

  const handleResend = async () => {
  setEmailError("");
  setEmailExistsError("");
  setSubmittedStep2(false);

  const ok = await sendSignupLink({ email, selectedOptions, optionLInput, postSignupRedirect });
  if (!ok) return;

  setEmailSent(true);
};

  const checkEmailExists = async (emailToCheck) => {
    setLoading(true);
    try {
      const response = await fetch("https://squid-app-6s2hc.ondigitalocean.app/check-email", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: emailToCheck }),
      });
      const data = await response.json();
      return Boolean(data.exists);
    } catch (error) {
      console.error("email check failed:", error);
      setEmailError("error checking email. please try again");
      return false;
    } finally {
      setLoading(false);
    }
  };

  // NEW: backend should send an email with a link to finish signup
  const sendSignupLink = async ({ email, selectedOptions, optionLInput, postSignupRedirect }) => {
    setLoading(true);
    try {
      const response = await fetch("https://squid-app-6s2hc.ondigitalocean.app/send-signup-link", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, selectedOptions, optionLInput, postSignupRedirect }),
      });

      if (!response.ok) throw new Error("failed to send signup email");
      return true;
    } catch (error) {
      console.error(error);
      setEmailError("couldn’t send the email. please try again.");
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleStep1Continue = async () => {
    if (selectedOptions.length === 0) return;

    // optional: save step 1 immediately
    await saveStepData({ selectedOptions, optionLInput });
    setStep(2);
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setSubmittedStep2(true);

    setEmailError("");
    setEmailExistsError("");

    const basicEmailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!email) return;

    if (email.length < 6 || !basicEmailRegex.test(email)) {
      setEmailError("enter a valid email address");
      return;
    }

    const emailTaken = await checkEmailExists(email);
    if (emailTaken) {
      setEmailExistsError("this email is already registered");
      return;
    }

    // save what they told us + redirect intent (so the emailed link can restore it)
    await saveStepData({ email, selectedOptions, optionLInput, postSignupRedirect });

    // send email with “finish signup” link
    const ok = await sendSignupLink({ email, selectedOptions, optionLInput, postSignupRedirect });
    if (!ok) return;

    setEmailSent(true);
  };

  return (
    <div className={`registration-page ${step === 1 ? "step-1" : ""}`}>
      {step === 1 && (
        <>
          <h1 className="page-title">what are you collecting?</h1>
          <h2 className="subheader">select your obsession(s)</h2>
          <div className="option-cards" role="group" aria-label="Collection categories">
            {options.map((option, index) => (
              <div
                key={index}
                className={`option-card ${selectedOptions.includes(option.title) ? "selected" : ""}`}
                onClick={() => toggleOption(option.title)}
                role="button"
                aria-pressed={selectedOptions.includes(option.title)}
                tabIndex="0"
              >
                <div className="emoji-icon" aria-hidden="true">{option.emoji}</div>
                <h3>{option.title}</h3>
                <p>{option.description}</p>
              </div>
            ))}
          </div>

          {selectedOptions.includes("my collection isn't listed") && (
            <div className="special-input-container">
              <label htmlFor="optionLInput" className="sr-only">describe your collection</label>
              <textarea
                id="optionLInput"
                value={optionLInput}
                onChange={(e) => setOptionLInput(e.target.value)}
                placeholder="I collect..."
                className="special-input"
                aria-label="Custom collection input"
              />
            </div>
          )}

         <div className="step1-footer">
  <button
    className="continue-button"
    onClick={handleStep1Continue}
    disabled={selectedOptions.length === 0}
  >
    continue
  </button>

  <p className="help-text">
    questions? email{" "}
    <a href="mailto:shaq@thecollectorsplatform.com" target="_blank" rel="noopener noreferrer">
      the founder
    </a>
    {" "}or learn more about t.c.p.{" "}
    <a href="/about" target="_blank" rel="noopener noreferrer">here</a>
  </p>
</div>

        </>
      )}

{step === 2 && (
  <main className="registration-step2">
    <div className="returning-wrap">
      
      {!emailSent ? (
        <form onSubmit={handleEmailSubmit} className="login-form" noValidate>
          <div className="input-group">
            <label htmlFor="email" className="sr-only">email</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setEmailError("");
                setEmailExistsError("");
                setSubmittedStep2(false);
              }}
              placeholder="email"
              autoComplete="email"
              inputMode="email"
            />
          </div>

          {(submittedStep2 && (!email || emailError || emailExistsError)) && (
            <p className={`status-message ${emailExistsError || emailError || !email ? "is-error" : "is-success"}`}>
              {!email ? "please enter your email." : emailError ? emailError : emailExistsError}
              {emailExistsError && (
                <>
                   <br />
                  <a
                    href="/returning"
                    onClick={(e) => {
                      e.preventDefault();
                      navigate("/returning");
                    }}
                  >
                    log in
                  </a>
                </>
              )}
            </p>
          )}

          <button className="submit-button" type="submit" disabled={loading}>
            {loading ? "sending..." : "sign up"}
          </button>

          <p className="development-note">
            your email will only be used to send a sign-up link.
            this is to protect the community and keep things safe.
            <br /><br />
            read more about how your data is used{" "}
            <a href="/about?section=data-usage" target="_blank" rel="noopener noreferrer">
              here
            </a>
            .
          </p>
        </form>
      ) : (
        <div className="login-form">
          <p className="success-message">
            a sign-up link has been sent.

            <br /><br />

            check spam or promotions if you don't
            see it within a minute.
          </p>

          <button className="submit-button" onClick={handleResend} disabled={loading}>
            {loading ? "sending..." : "resend email"}
          </button>

          {emailError && <p className="status-message is-error">{emailError}</p>}
        </div>
      )}
    </div>
  </main>
)}
    </div>
  );
};

export default RegistrationPage;
